import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { withTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Col, Container, Form, Input, Modal, Row } from "reactstrap";
import { Loader } from "src/components";
import { TicketFieldSetting } from "src/sdk/models/Organisation";
import { getTicketInitialValue, Ticket } from "src/sdk/models/Ticket";
import { TicketService } from "src/services/TicketService";
import { FeedbackColumns } from "./FeedbackColumns";
import { formatToEst } from "src/common/helper";

export interface TicketingDetailsByTicketIdProps {
  ticketId: string;
  modal_xlarge: boolean;
  setmodal_xlarge: (value: boolean) => void;
  tog_xlarge: () => void;
  fields: TicketFieldSetting[];
}

const service = new TicketService();
export const TicketingDetailsByTicketId: React.FC<TicketingDetailsByTicketIdProps> = ({
  ticketId,
  modal_xlarge,
  setmodal_xlarge,
  tog_xlarge,
  fields
}) => {
  const history = useHistory();

  const [ticket, setTicket] = useState<Ticket>(getTicketInitialValue());
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<"description" | "feedback">("description");

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: ticket,
    onSubmit: () => { },
  });

  useEffect(() => {
    if (!ticketId) return;
    setLoading(true);
    service.getTicketByTicketId(ticketId)
      .then((res: any) => setTicket(res.data.item))
      .catch((err: any) => console.error(err))
      .finally(() => setLoading(false));
  }, [ticketId]);

  const handleClose = () => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete("ticketId");
    history.replace({
  pathname: "/ticketing",
  search: searchParams.toString(),
  state: history.location.state,
});

    setmodal_xlarge(false);
  };

  const showField = (fieldName: string) => fields.find(f => f.name === fieldName)?.showOnForm;

  return (
    <React.Fragment>
      <div className="container-fluid">
        <Modal
          size="xl"
          isOpen={modal_xlarge}
          toggle={handleClose}
          scrollable={true}
        >
          <div className="modal-header d-flex align-items-center justify-content-between px-4">
            <h5 className="modal-title mt-0 mb-0">{ticketId}</h5>
            <button
              onClick={handleClose}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#808080"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#AAAAAA'}
              data-dismiss="modal"
              style={{
                backgroundColor: "#AAAAAA",
                color: "#FFFFFF",
                border: 0,
                borderRadius: 60,
                gap: 5,
                width: 100,
                paddingTop: 5,
                paddingBottom: 5,
                paddingLeft: 20,
                paddingRight: 20,
                display: "flex"
              }}
              type="button"
            >
              <i className={`bi-x-circle`} />
              Close
            </button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
                <Loader loading={true} />
              </div>
            ) : (
              <Form onSubmit={(e: any) => { e.preventDefault(); validation.handleSubmit(); return false; }}>
                <Container fluid className="p-3">

                  <Row className="mb-3">
                    {showField("facilityName") && (
                      <Col md={4}>
                        <label className="form-label"><i className="bi bi-building text-success me-1"></i> Facility</label>
                        <Input type="text" value={ticket.facilityName ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("status") && (
                      <Col md={4}>
                        <label className="form-label">Status</label>
                        <Input type="text" value={ticket.status ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("priority") && (
                      <Col md={4}>
                        <label className="form-label">Priority</label>
                        <Input type="text" value={ticket.priority ?? "-"} readOnly disabled />
                      </Col>
                    )}
                  </Row>

                  <Row className="mb-3">
                    {showField("radiologist") && (
                      <Col md={4}>
                        <label className="form-label"><i className="bi bi-person text-success me-1"></i> Radiologist</label>
                        <Input type="text" value={ticket.radiologist ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("category") && (
                      <Col md={4}>
                        <label className="form-label">Category</label>
                        <Input type="text" value={ticket.category ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("subCategory") && (
                      <Col md={4}>
                        <label className="form-label">Sub-Category</label>
                        <Input type="text" value={ticket.subCategory ?? "-"} readOnly disabled />
                      </Col>
                    )}
                  </Row>

                  <Row className="mb-4">
                    {showField("contact") && (
                      <Col md={6}>
                        <label className="form-label"><i className="bi bi-telephone text-success me-1"></i> Contact</label>
                        <Input type="text" value={ticket.contact ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("supportArea") && (
                      <Col md={6}>
                        <label className="form-label">Support Area</label>
                        <Input type="text" value={ticket.supportArea ?? "-"} readOnly disabled />
                      </Col>
                    )}
                  </Row>

                  <hr className="my-4" />

                  <Row className="mb-3">
                    {showField("createdBy") && (
                      <Col md={6}>
                        <label className="form-label">Ticket Created By</label>
                        <Input type="text" value={ticket.createdBy ?? "-"} readOnly disabled />
                      </Col>
                    )}
                    {showField("createdAt") && (
                      <Col md={6}>
                        <label className="form-label">Ticket Created At</label>
                        <Input type="text"  value={formatToEst(ticket.createdAt)} readOnly disabled />
                      </Col>
                    )}
                  </Row>

                  {showField("subject") && (
                    <Row className="mb-5">
                      <Col md={12}>
                        <label className="form-label">Subject</label>
                        <Input type="textarea" value={ticket.subject ?? "-"} readOnly disabled rows={6} />
                      </Col>
                    </Row>
                  )}

                  <hr style={{ marginTop: '80px', marginBottom: '16px' }} />

                  <div className="d-flex gap-4 mb-1 pb-2">
                    <label
                      className={`form-label mb-0 cursor-pointer ${activeTab === "description" ? "text-success" : "fw-bold"}`}
                      onClick={() => setActiveTab("description")}
                    >
                      Description
                    </label>
                    <label
                      className={`form-label mb-0 cursor-pointer ${activeTab === "feedback" ? "text-success" : "fw-bold"}`}
                      onClick={() => setActiveTab("feedback")}
                    >
                      Feedback ({ticket.feedbackItems?.length || 0})
                    </label>
                  </div>

                  {activeTab === "description" && (
                    <Row className="mb-3">
                      <Col md={12}>
                        <Input type="textarea" value={ticket.description ?? ""} readOnly disabled rows={6} />
                      </Col>
                    </Row>
                  )}

                  {activeTab === "feedback" && (
                    <DataTable
                      columns={FeedbackColumns()}
                      data={ticket.feedbackItems || []}
                      noDataComponent="No feedback available."
                      striped
                      highlightOnHover
                      responsive
                      dense
                    />
                  )}
                </Container>
              </Form>
            )}
          </div>
        </Modal>
      </div>
    </React.Fragment>
  );
}

export default withTranslation()(TicketingDetailsByTicketId);
